// Rain sound: a soft wash (low-passed noise, no high hiss), random drop ticks, and a layer for where you are: gutter
// drips in alleys, drumming on the roof in a car, a muffled distant wash under a roof. Loudness follows the weather
// (weather.ts). Procedural Web Audio like the rest of audio.ts; nothing is loaded.
import type { RainPlace } from './weather';

/** per place: wash lowpass (Hz), wash gain, drop ticks per second at full rain, drips per second, roof drumming */
const PLACE: Record<RainPlace, { tone: number; wash: number; ticks: number; drips: number; drum: boolean }> = {
  street: { tone: 2400, wash: 0.018, ticks: 14, drips: 0, drum: false },
  alley: { tone: 1800, wash: 0.013, ticks: 8, drips: 1.6, drum: false },
  covered: { tone: 700, wash: 0.009, ticks: 0, drips: 0.5, drum: false },
  car: { tone: 500, wash: 0.010, ticks: 0, drips: 0, drum: true },
};

export class RainSound {
  private wash: BiquadFilterNode; private washG: GainNode; private bus: GainNode;
  private brown: AudioBuffer; private white: AudioBuffer;
  private place: RainPlace = 'street'; private intensity = 0.5; private ahead = 0;

  constructor(private ctx: AudioContext, out: AudioNode, white: AudioBuffer) {
    this.white = white;
    // 4 s of brown-ish noise: a deeper, softer bed than white noise, and long enough that the loop doesn't show
    const len = ctx.sampleRate * 4, b = ctx.createBuffer(1, len, ctx.sampleRate), d = b.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) { last = (last + 0.04 * (Math.random() * 2 - 1)) / 1.04; d[i] = last * 3.2; }
    this.brown = b;
    this.bus = ctx.createGain(); this.bus.connect(out);
    const src = ctx.createBufferSource(); src.buffer = b; src.loop = true;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 120;
    this.wash = ctx.createBiquadFilter(); this.wash.type = 'lowpass'; this.wash.frequency.value = PLACE.street.tone; this.wash.Q.value = 0.4;
    this.washG = ctx.createGain(); this.washG.gain.value = 0;
    src.connect(hp).connect(this.wash).connect(this.washG).connect(this.bus); src.start();
  }

  /** Call every frame (cheap): crossfades to the place, follows the intensity, and schedules drops ~0.25 s ahead. */
  update(place: RainPlace, intensity: number) {
    const ctx = this.ctx, t = ctx.currentTime, p = PLACE[place];
    if (place !== this.place || Math.abs(intensity - this.intensity) > 0.02) {
      this.wash.frequency.setTargetAtTime(p.tone * (0.85 + 0.3 * intensity), t, 0.35);
      this.washG.gain.setTargetAtTime(p.wash * (0.35 + 0.65 * intensity), t, 0.6);
      this.place = place; this.intensity = intensity;
    }
    // random events for the next quarter second, so frame timing never makes them clump
    if (this.ahead < t) this.ahead = t;
    const end = t + 0.25;
    while (this.ahead < end) {
      const step = 0.02; this.ahead += step;
      const at = this.ahead + Math.random() * step;
      if (Math.random() < p.ticks * intensity * step) this.tick(at, intensity);
      if (Math.random() < p.drips * step) this.drip(at);
      if (p.drum && Math.random() < 22 * intensity * step) this.drum(at, intensity);
    }
  }

  /** one raindrop on the pavement: a few ms of band-passed noise at a random pitch */
  private tick(at: number, k: number) {
    const ctx = this.ctx, s = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    s.buffer = this.white; f.type = 'bandpass'; f.frequency.value = 1800 + Math.random() * 3200; f.Q.value = 3;
    const v = (0.004 + Math.random() * 0.01) * (0.5 + 0.5 * k);
    g.gain.setValueAtTime(v, at); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.012 + Math.random() * 0.02);
    s.connect(f).connect(g).connect(this.bus); s.start(at, Math.random() * 0.9, 0.05);
  }
  /** a gutter drip into a puddle: a short falling sine "plink" */
  private drip(at: number) {
    const ctx = this.ctx, o = ctx.createOscillator(), g = ctx.createGain(), f0 = 700 + Math.random() * 700;
    o.type = 'sine'; o.frequency.setValueAtTime(f0, at); o.frequency.exponentialRampToValueAtTime(f0 * 0.55, at + 0.07);
    g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(0.012 + Math.random() * 0.01, at + 0.004); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.09);
    o.connect(g).connect(this.bus); o.start(at); o.stop(at + 0.1);
  }
  /** a drop drumming on the car roof: a dull low thud */
  private drum(at: number, k: number) {
    const ctx = this.ctx, s = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    s.buffer = this.brown; f.type = 'lowpass'; f.frequency.value = 260 + Math.random() * 260;
    g.gain.setValueAtTime(0.03 + 0.03 * k * Math.random(), at); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
    s.connect(f).connect(g).connect(this.bus); s.start(at, Math.random() * 3.5, 0.06);
  }
}
